import type { EnabledPages } from 'src/content/enabled-pages-helper';
import type { LanguageIsoCode } from 'src/enums/language';
import type { ClubPageFeatures, NewClubPageFeatures } from 'src/models/cms/club-page-features';
import type { NavigationMenuContent } from 'src/models/cms/navigation-menu-content';
import type { PromotionContent } from 'src/models/cms/promotions-content';
import type { ClubLocation, SharedContent } from 'src/models/cms/shared-content';

export type ClubHeaderContent = {
  headerMenuItems?: Queries.HeaderLevelOneMenuItem[];
  headerSecondaryUpperMenu?: Queries.HeaderSecondaryUpperMenuItem[];
  headerButtonPrimary?: Queries.RedesignButton;
  headerButtonSecondary?: Queries.RedesignButton;
  headerOtherText?: Queries.HeaderOtherText;
  headerPromoRibbon?: Queries.HeaderPromoRibbon;
};

export type NewSharedData = {
  clubEmail: string;
  clubLocation: ClubLocation;
  phoneNumbers: {
    membersPhoneNumber: string;
    nonMembersPhoneNumber: string;
    clubPhoneNumbers: { description?: string; number?: string }[];
  };
  tourEmailAddresses?: string[];
  enquiryEmailAddresses: string[];
  nonMembersEmail: string;
  clubThumbnailImage: string;
  comingSoonClubFinder: boolean;
  clubDefaultPopup?: Queries.PromoBlockWithImageClubDefaultPagePopup;
  clubFacts?: Queries.ClubFact[];
  statusClubFinder: string;
};

export type ClubCmsRecord = {
  clubDetails: any;
  features: ClubPageFeatures;
  newFeatures: NewClubPageFeatures;
  sharedContent: SharedContent;
  newSharedContent: NewSharedData;
  navigationMenuContent: NavigationMenuContent;
  promotionsContent: Record<LanguageIsoCode, PromotionContent>[];
  newPromotionsContent: Record<LanguageIsoCode, Queries.NewClubPromotion>[];
  sharedLandingContent: Queries._SharedLandingContentFragment;
  pages: EnabledPages;
  headerContent: ClubHeaderContent;
  brandTransitionWelcomeContent: Queries._BrandTransitionWelcomeContentFragment | null;
};

type ClubJsonBackedKey = Exclude<keyof ClubCmsRecord, 'promotionsContent' | 'newPromotionsContent'>;

type ClubCmsPageQueryData = {
  [K in ClubJsonBackedKey]: { nodes: readonly { childClubJson: ClubCmsRecord[K] | null }[] };
} & {
  promotionsContent?: {
    nodes: readonly {
      childPromotionsJson: ClubCmsRecord['promotionsContent'][number] | null;
    }[];
  };
  newPromotionsContent?: {
    nodes: readonly {
      childNewPromotionsJson: ClubCmsRecord['newPromotionsContent'][number] | null;
    }[];
  };
};

// Some pages do not require this information. This tripwire is a sanity check for page queries.
const createMissingPromotionsTripwire = <T,>(): T[] =>
  new Proxy([] as T[], {
    get(target, prop, receiver) {
      if (typeof prop === 'symbol' || prop === 'length') {
        return Reflect.get(target, prop, receiver) as unknown;
      }
      throw new Error('Promotion content missing from page query.');
    },
  });

export const unwrapClubCmsRecordFromPageQuery = (data: unknown): ClubCmsRecord => {
  const pageData = data as ClubCmsPageQueryData;

  const promotionsContent = pageData.promotionsContent
    ? pageData.promotionsContent.nodes.map((node) => node.childPromotionsJson!)
    : createMissingPromotionsTripwire<ClubCmsRecord['promotionsContent'][number]>();

  const newPromotionsContent = pageData.newPromotionsContent
    ? pageData.newPromotionsContent.nodes.map((node) => node.childNewPromotionsJson!)
    : createMissingPromotionsTripwire<ClubCmsRecord['newPromotionsContent'][number]>();

  return {
    clubDetails: pageData.clubDetails.nodes[0].childClubJson!,
    features: pageData.features.nodes[0].childClubJson!,
    newFeatures: pageData.newFeatures.nodes[0].childClubJson!,
    sharedContent: pageData.sharedContent.nodes[0].childClubJson!,
    newSharedContent: pageData.newSharedContent.nodes[0].childClubJson!,
    navigationMenuContent: pageData.navigationMenuContent.nodes[0].childClubJson!,
    promotionsContent,
    newPromotionsContent,
    sharedLandingContent: pageData.sharedLandingContent.nodes[0].childClubJson!,
    pages: pageData.pages.nodes[0].childClubJson!,
    headerContent: pageData.headerContent.nodes[0].childClubJson!,
    brandTransitionWelcomeContent:
      pageData.brandTransitionWelcomeContent.nodes[0]?.childClubJson ?? null,
  };
};
